import TrackPlayer, { State, Event, } from 'react-native-track-player';
import { analytics, usePlaybackStateStore, usePlaylistStateStore, PlaybackEventEmitter, PlaybackEvent, FileType, PositionType, PositionTimeUnit, JumpType, PlaybackState, AnalyticsEventEmitter, Topic, } from '@annunciate/core';
import { onPlaybackStatus, onAVJump, onAVStarted, onAVStopped, onSleepTimerCheck, } from '@annunciate/core/dist/utils/player';
import { fetchPlaylist, onProgressUpdate, syncProgress } from '@annunciate/query';
import { getTrackIdAndPosition } from '../audio';
import { onPause, onStart, onStop, onAttemptAdvancePlaylist, } from "../player";
import { onRefetch } from '../player/onRefetch';
const { getState: getPlaybackState, setState: setPlaybackState, } = usePlaybackStateStore.getState();
export async function TrackPlayerService(client, eventSubscriberCallback = () => { }) {
    AnalyticsEventEmitter.on(Topic.SYNC_PROGRESS, async (message) => {
        try {
            await syncProgress(client);
        }
        catch (err) {
        }
    });
    eventSubscriberCallback(AnalyticsEventEmitter);
    const applyAVJump = async (id, _type, amount) => {
        const idAndPosition = await getTrackIdAndPosition();
        if (idAndPosition === null)
            return;
        const { id: trackId, position } = idAndPosition;
        if (trackId === id) {
            let newPosition = position + amount;
            if (newPosition < 0)
                newPosition = 0;
            const { content, isPlaying } = usePlaybackStateStore.getState();
            analytics.emitContentEvent("pausing", {
                asset_id: String(id),
                position
            });
            analytics.emitPlaybackEvent("seekStarted", {
                position,
                seek_position: newPosition,
            });
            await TrackPlayer.seekTo(newPosition);
            if (isPlaying) {
                await analytics.emitContentEvent("playing", {
                    content: content || undefined,
                    position: newPosition,
                });
            }
            return {
                start: position,
                end: position + amount,
            };
        }
        return undefined;
    };
    TrackPlayer.addEventListener(Event.RemotePlay, () => {
        PlaybackEventEmitter.emit(PlaybackEvent.START, {
            content: usePlaybackStateStore.getState().content,
        });
    });
    TrackPlayer.addEventListener(Event.RemotePause, () => PlaybackEventEmitter.emit(PlaybackEvent.PAUSE));
    TrackPlayer.addEventListener(Event.RemoteJumpForward, onAVJump(JumpType.FORWARD, applyAVJump));
    TrackPlayer.addEventListener(Event.RemoteJumpBackward, onAVJump(JumpType.BACKWARD, applyAVJump));
    TrackPlayer.addEventListener(Event.RemoteStop, () => TrackPlayer.pause());
    TrackPlayer.addEventListener(Event.PlaybackActiveTrackChanged, async ({ lastTrack, lastPosition, track, }) => {
        if (lastTrack && lastTrack.id !== track?.id) {
            setPlaybackState(lastTrack.id, PlaybackState.NONE);
            await onAVStopped(lastTrack.id, lastPosition);
        }
        if (track) {
            const id = track.id;
            const lastState = getPlaybackState(id);
            const rawPosition = await TrackPlayer.getProgress();
            const position = Math.floor(rawPosition?.position || 0);
            await onAVStarted(id, position);
        }
    });
    TrackPlayer.addEventListener(Event.PlaybackState, async (event) => {
        const { state } = event;
        const idAndPosition = await getTrackIdAndPosition();
        analytics.onStateChange(event);
        const isPlaying = state === State.Playing;
        const isError = state === State.Error;
        const isStopped = state === State.Paused || state === State.Stopped;
        const isEnded = state === State.Ended;
        if (isError) {
            console.error("Playback state is in error");
            PlaybackEventEmitter.emit(PlaybackEvent.RESTART);
            return;
        }
        if (isPlaying) {
            usePlaybackStateStore.setState({ isPlaying: true });
        }
        else if (isStopped || isEnded) {
            usePlaybackStateStore.setState({ isPlaying: false });
        }
        if (idAndPosition?.id) {
            const isBuffering = state === State.Buffering;
            const currentState = getPlaybackState(idAndPosition.id);
            const isStateNone = currentState === PlaybackState.NONE;
            const isStateStopped = currentState === PlaybackState.STOPPED;
            if (isBuffering && !isStateStopped) {
                setPlaybackState(idAndPosition.id, PlaybackState.LOADING);
            }
            else if (isPlaying) {
                setPlaybackState(idAndPosition.id, PlaybackState.PLAYING);
            }
            else if (isStopped && !isStateNone) {
                setPlaybackState(idAndPosition.id, PlaybackState.STOPPED);
            }
            else if (isEnded) {
                setPlaybackState(idAndPosition.id, PlaybackState.NONE);
            }
        }
        if (state === State.Ended) {
            onAttemptAdvancePlaylist(client, idAndPosition?.id);
        }
    });
    TrackPlayer.addEventListener(Event.PlaybackProgressUpdated, async ({ position, duration }) => {
        onSleepTimerCheck();
        const typedPosition = {
            type: PositionType.TIME,
            unit: PositionTimeUnit.SECONDS,
            value: position,
        };
        const isPlaying = usePlaybackStateStore.getState().isPlaying;
        await Promise.all([
            onProgressUpdate(client, typedPosition),
            onPlaybackStatus({ position, duration }),
            isPlaying && analytics.emitAudioEvent("heartbeat", {
                position,
                total_length: duration,
            }),
        ]);
    });
    const onStartWrapped = (...args) => onStart(client, ...args);
    PlaybackEventEmitter.on(PlaybackEvent.START, onStartWrapped);
    PlaybackEventEmitter.on(PlaybackEvent.PAUSE, onPause);
    PlaybackEventEmitter.on(PlaybackEvent.STOP, onStop);
    PlaybackEventEmitter.on(PlaybackEvent.SET_PLAYBACK_RATE, async ({ rate }) => {
        TrackPlayer.setRate(rate);
    });
    PlaybackEventEmitter.on(PlaybackEvent.SCRUB, async ({ content, to }) => {
        if (to.type === PositionType.TIME &&
            content.asset?.type === FileType.AUDIO) {
            const toTime = to.value;
            const isPlaying = usePlaybackStateStore.getState().isPlaying;
            await analytics.emitContentEvent("pausing", { content });
            await analytics.emitPlaybackEvent("seekStarted", {
                seek_position: toTime,
            });
            try {
                await TrackPlayer.seekTo(toTime);
            }
            catch (err) {
                console.error(err);
            }
            if (isPlaying) {
                await analytics.emitContentEvent("playing", {
                    content,
                    position: toTime,
                });
            }
        }
        else if (to.type === PositionType.EBOOK &&
            content.asset?.type === FileType.EBOOK) {
            console.log("Scrub is handled within the Reader for ebooks");
        }
        else {
            console.warn(`Scrub has not been implemented for file type
          or there is a mismatch between asset type and position type`, to.type, content.asset?.type);
        }
    });
    PlaybackEventEmitter.on(PlaybackEvent.JUMP_FORWARD, onAVJump(JumpType.FORWARD, applyAVJump));
    PlaybackEventEmitter.on(PlaybackEvent.JUMP_BACKWARD, onAVJump(JumpType.BACKWARD, applyAVJump));
    PlaybackEventEmitter.on(PlaybackEvent.ACTIVE_PLAYLIST_CONTEXT_UPDATED, async () => {
        const timestamp = new Date();
        const { activePlaylistContext } = usePlaylistStateStore.getState();
        if (activePlaylistContext) {
            const items = await fetchPlaylist(client, activePlaylistContext);
            const ids = items.map(i => i.item.id);
            usePlaylistStateStore
                .getState()
                .setActivePlaylistItems(ids);
        }
        analytics.emitStartedEvent(timestamp);
    });
    PlaybackEventEmitter.on(PlaybackEvent.ATTEMPT_ADVANCE_PLAYLIST, async (id) => {
        onAttemptAdvancePlaylist(client, id);
    });
    PlaybackEventEmitter.on(PlaybackEvent.VIDEO_ENDED, async (id) => {
        onAttemptAdvancePlaylist(client, id);
    });
    PlaybackEventEmitter.on(PlaybackEvent.RESTART, async (initialPosition) => {
        const { content, restartAttempts } = usePlaybackStateStore.getState();
        if (!content)
            return;
        if (restartAttempts > 10) {
            console.error("Too many restart attempts");
            await PlaybackEventEmitter.emit(PlaybackEvent.STOP);
            await PlaybackEventEmitter.emit(PlaybackEvent.UNKOWN_ERROR, {
                content,
                message: 'An error occurred when attempting playback. Please try again or contact support.',
            });
            usePlaybackStateStore.setState({
                isPlaying: false,
                content: undefined,
                states: {},
                restartAttempts: 0,
                initialPosition: undefined,
            });
            return;
        }
        usePlaybackStateStore.setState({ restartAttempts: restartAttempts + 1 });
        await onRefetch(client, { content, initialPosition });
    });
}
;
//# sourceMappingURL=TrackPlayerService.js.map